// src/features/clients/pages/ClientHistoryPage.tsx
// ============================================================================
// v1.7.0 - Etiquetas de cliente (tags)
//   - Sección visual para ver/agregar/quitar etiquetas
//   - Write directo a Firestore con arrayUnion/arrayRemove
//   - ES se actualiza automáticamente via Cloud Function existente
//
// v1.6.0 - Ajuste de puntos inline (Pro) + Historial de movimientos + InfluencerCode extraído
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import Layout from '../../../components/layout/Layout';
import {
  History, RefreshCw, ArrowLeft, Star, MessageCircle, Clock, CalendarClock, Loader2,
  Mail, Users, Cake, CalendarPlus, DollarSign, BellOff, Bell, X, UserMinus,
  Coins, Lock, Plus, Minus, Check, Crown, ArrowUpDown, TrendingUp, TrendingDown, Gift,
  CreditCard, // ✅ Gift Cards
  Tag,  // ✅ v1.7.0
  Pencil, Phone,  // ✅ v1.8.0 - Editar cliente
  Archive, ArchiveRestore,  // ✅ v1.8.0 - Archivar cliente
  Ban,  // «No acumula» (19-oct-2026)
  BadgeCheck,  // Membresías (20-oct-2026)
} from 'lucide-react';
import BrandLoader from '../../../components/common/BrandLoader';
import RegisterVisitModal from '../components/RegisterVisitModal';
import ClientCardModal from '../components/ClientCardModal';   // tarjeta QR del cliente (23-oct-2026)
import { pctCashbackCliente } from '../../../utils/cashbackEspecial';
import CancelVisitModal from '../components/CancelVisitModal';
import AccrualBlockModal from '../components/AccrualBlockModal';
import { accrualBlocksApi, fmtHasta, REASON_LABEL, type AccrualBlock } from '../../../api/accrualBlocksApi';
import SellMembershipModal from '../../memberships/components/SellMembershipModal';
import { membershipsApi, fmtVence, type ContactMembership } from '../../../api/membershipsApi';
import { toast } from 'react-hot-toast';
import type { WhatsAppClient, CheckIn } from '../../../types/user';
import { DateDisplay } from '../../../components/common/DateTimeDisplay';
import { formatBirthday } from '../../../utils/dates';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import ClientAvatar from '../../../components/common/ClientAvatar';
import { clientsApi } from '../../../api/clientsApi';
import GoogleReviewChip from '../components/GoogleReviewChip';
import { isPro as checkIsPro } from '../../../utils/planPermissions';

// ✅ Importamos nuestro nuevo componente limpio
import InfluencerCodeManager from '../components/InfluencerCodeManager';
import GiftBalanceCard from '../components/GiftBalanceCard';

// --- PRESETS DE AJUSTE DE PUNTOS ---
const POINTS_ADJUST_PRESETS = [5, 10, 25, 50, 100];

// --- v1.8.0: Editar cliente (mismas opciones que el registro) ---
// Catálogo compartido (src/lib/countries.ts); países extra solo con el flag intl_phones.
import { countriesFor, PHONE_COUNTRIES, storedPhoneFor as storedPhoneForCountry } from '../../../lib/countries';
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// --- INTERFAZ DE MOVIMIENTOS DE PUNTOS ---
interface PointsMovement {
  id: string;
  points: number;
  previousPoints: number;
  newPoints: number;
  operation: 'add' | 'subtract';
  createdAt: Date;
  source?: string;
  description?: string;
}

const getSourceLabel = (source?: string, description?: string): string => {
  if (description) return description;
  switch (source) {
    case 'client_history': return 'Ajuste desde historial';
    case 'kiosk_purchase': return 'Compra en kiosco';
    case 'referral_reward': return 'Recompensa por referir';
    case 'referral_bonus': return 'Bonus de bienvenida (referido)';
    case 'gift_card': return 'Gift Card 🎁';
    default: return 'Gestión de puntos';
  }
};

// --- HELPERS ---
const getFeedbackColor = (feedback?: 'excelente' | 'bueno' | 'neutral' | 'malo' | 'omitted') => { 
  switch(feedback){ 
    case 'excelente': return 'text-yellow-500'; 
    case 'bueno': return 'text-green-500'; 
    case 'neutral': return 'text-blue-500'; 
    case 'malo': return 'text-red-500'; 
    case 'omitted': return 'text-content-3'; 
    default: return 'text-content-3'; 
  } 
};

const getFeedbackText = (feedback?: 'excelente' | 'bueno' | 'neutral' | 'malo' | 'omitted') => { 
  switch(feedback){ 
    case 'excelente': return 'Excelente'; 
    case 'bueno': return 'Bueno'; 
    case 'neutral': return 'Neutral'; 
    case 'malo': return 'Malo'; 
    case 'omitted': return 'No realizó calificación'; 
    default: return 'N/A'; 
  } 
};

const getEmailLink = (email: string | null): string => { 
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '#'; 
  return `mailto:${email}`; 
};

// --- COMPONENT ---
const ClientHistoryPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addError, handleFirebaseError, clearErrors } = useError();
  const [client, setClient] = useState<WhatsAppClient | null>(null);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [visitLoading, setVisitLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreVisits, setHasMoreVisits] = useState(true);
  const [totalVisits, setTotalVisits] = useState(0);
  const [togglingOptOut, setTogglingOptOut] = useState(false);
  const [showOptOutModal, setShowOptOutModal] = useState(false);

  // Estado para ajuste de puntos inline
  const [showPointsAdjust, setShowPointsAdjust] = useState(false);
  const [showRegisterVisit, setShowRegisterVisit] = useState(false);
  const [showCard, setShowCard] = useState(false);   // tarjeta QR del cliente
  // Cancelar compra (Bramarket 10-oct-2026): dueño y gerente; el servidor también lo exige.
  const [cancelCheckIn, setCancelCheckIn] = useState<CheckIn | null>(null);
  const canCancelVisit = currentUser?.baseRole === 'owner' || currentUser?.baseRole === 'manager';
  // «Quién no acumula» (19-oct-2026, #1465 Silvestre): dueño y gerente; el servidor exige loyalty:manage.
  const canBlock = !currentUser?.baseRole || currentUser.baseRole === 'owner' || currentUser.baseRole === 'manager';
  const [accrualBlock, setAccrualBlock] = useState<AccrualBlock | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  // Membresías F1 (20-oct-2026): vender/renovar/cancelar desde la ficha (dueño y gerente; el servidor exige manage + PRO + flag).
  const [membership, setMembership] = useState<ContactMembership | null>(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [cancellingMemb, setCancellingMemb] = useState(false);
  const membershipsOn = !!currentUser?.memberships;
  useEffect(() => {
    if (!clientId || !membershipsOn) return;
    let vivo = true;
    membershipsApi.forContact(clientId).then((f) => { if (vivo) setMembership(f.current); }).catch(() => {});
    return () => { vivo = false; };
  }, [clientId, membershipsOn]);
  const cancelarMembresia = async () => {
    if (!clientId || !membership) return;
    const motivo = window.prompt(`¿Cancelar la membresía ${membership.plan_name}? Motivo (opcional):`);
    if (motivo === null) return;
    setCancellingMemb(true);
    try { await membershipsApi.cancel(clientId, motivo); const f = await membershipsApi.forContact(clientId); setMembership(f.current); toast.success('Membresía cancelada'); }
    catch { toast.error('No se pudo cancelar'); }
    finally { setCancellingMemb(false); }
  };
  useEffect(() => {
    if (!clientId) return;
    let vivo = true;
    accrualBlocksApi.forContact(clientId).then((b) => { if (vivo) setAccrualBlock(b); }).catch(() => {});
    return () => { vivo = false; };
  }, [clientId]);
  const quitarBloqueo = async () => {
    if (!clientId || !window.confirm('¿Vuelve a acumular visitas y a poder canjear?')) return;
    setUnblocking(true);
    try { await accrualBlocksApi.remove(clientId); setAccrualBlock(null); toast.success('Vuelve a acumular'); }
    catch { toast.error('No se pudo quitar'); }
    finally { setUnblocking(false); }
  };
  const [pointsInputValue, setPointsInputValue] = useState('');
  const [pointsOperation, setPointsOperation] = useState<'add' | 'subtract'>('add');
  const [notifyWhatsApp, setNotifyWhatsApp] = useState(true);
  const [pointsAdjustLoading, setPointsAdjustLoading] = useState(false);
  const [showCustomPointsInput, setShowCustomPointsInput] = useState(false);

  // Estado para historial de movimientos de puntos
  const [pointsMovements, setPointsMovements] = useState<PointsMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  // ── v1.7.0: Estado para etiquetas ──
  const [clientTags, setClientTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSaving, setTagSaving] = useState(false);

  // ── v1.8.0: Estado para archivar (soft-delete reversible) ──
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // ── v1.8.0: Estado para editar datos del cliente ──
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCountryCode, setEditCountryCode] = useState('+52');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editGender, setEditGender] = useState('');
  const [editBirthDay, setEditBirthDay] = useState('');
  const [editBirthMonth, setEditBirthMonth] = useState('');

  const VISITS_PER_PAGE = 10;
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  const isCashback = loyaltyMode === 'cashback';

  // Verificación de plan Pro
  const isAdmin = currentUser?.role === 'admin';
  const hasProAccess = checkIsPro(currentUser?.subscription) || isAdmin;

  // --- Fetch ---
  const fetchClientData = useCallback(async (page: number = 1, isNextPage: boolean = false) => {
    if (!clientId || !currentUser?.businessId) return;
    try {
      isNextPage ? setVisitLoading(true) : setLoading(true);
      clearErrors();

      // Detalle del cliente solo en la 1ª página; las visitas siempre (paginadas).
      if (!isNextPage) {
        const cli = await clientsApi.getClient(clientId);
        setClient(cli);
        const full = await clientsApi.get(clientId); // trae etiquetas
        setClientTags(full.tags || []);
      }

      const vp = await clientsApi.visitsPaged(clientId, page, VISITS_PER_PAGE);
      const visits = vp.items || [];
      setTotalVisits(vp.total);
      setHasMoreVisits(vp.hasMore);
      setCheckIns(prev => isNextPage ? [...prev, ...visits] : visits);
      
      if (isNextPage && visits.length > 0) {
        setCurrentPage(prev => prev + 1);
      } else if (!isNextPage) {
        setCurrentPage(1);
      }

    } catch (error) { 
      console.error('Error al obtener datos del cliente:', error); 
      handleFirebaseError(error, 'clientHistory');
    } finally { 
      setLoading(false);
      setVisitLoading(false);
    }
  }, [clientId, currentUser?.businessId, clearErrors, addError, handleFirebaseError]);

  useEffect(() => { 
    if (clientId) { fetchClientData(1, false); }
    return () => { if (clearErrors) clearErrors(); }; 
  }, [clientId, fetchClientData, clearErrors]);

  const reloadVisits = useCallback(() => fetchClientData(1, false), [fetchClientData]);
  
  const loadMoreVisits = useCallback(() => {
    if (!clientId || visitLoading || !hasMoreVisits) return;
    fetchClientData(currentPage + 1, true);
  }, [clientId, visitLoading, hasMoreVisits, currentPage, fetchClientData]);

  const formatPoints = (value: number): string => {
    if (isCashback) return `$${value.toFixed(2)}`;
    return `${Math.floor(value)} pts`;
  };

  // ============================================================================
  // FETCH MOVIMIENTOS DE PUNTOS
  // ============================================================================
  const fetchPointsMovements = useCallback(async () => {
    if (!clientId) return;
    setMovementsLoading(true);
    try {
      // Ledger NO-visita (ajustes manuales, bonos de referido, gift cards) desde base.
      const rows = await clientsApi.movements(clientId, 20);
      const movements: PointsMovement[] = rows.map(m => ({
        id: m.id,
        points: m.points,
        previousPoints: m.previousPoints,
        newPoints: m.newPoints,
        operation: m.operation,
        createdAt: m.createdAt,
        source: m.source,
        description: m.description,
      }));
      setPointsMovements(movements);
    } catch (error) {
      console.error('Error fetching movements:', error);
      setPointsMovements([]);
    } finally {
      setMovementsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      fetchPointsMovements();
    }
  }, [clientId, fetchPointsMovements]);

  // ============================================================================
  // AJUSTE DE PUNTOS INLINE
  // ============================================================================
  const handlePointsAdjust = async () => {
    if (!client || !clientId || !currentUser?.businessId || !currentUser?.uid || pointsAdjustLoading) return;

    const inputNum = parseFloat(pointsInputValue);
    if (isNaN(inputNum) || inputNum <= 0) {
      toast.error(isCashback ? 'Ingresa un monto válido' : 'Ingresa una cantidad válida');
      return;
    }

    const currentPoints = client.points ?? 0;
    let newTotalPoints: number;

    if (pointsOperation === 'add') {
      newTotalPoints = parseFloat((currentPoints + inputNum).toFixed(2));
    } else {
      if (currentPoints < inputNum) {
        toast.error('Saldo insuficiente para restar esa cantidad');
        return;
      }
      newTotalPoints = parseFloat((currentPoints - inputNum).toFixed(2));
    }

    setPointsAdjustLoading(true);

    const adjustPromise = (async () => {
      // Ajuste ATÓMICO server-side (suma/resta sobre el ledger). points firmado.
      const out = await clientsApi.adjust({
        contactId: clientId,
        points: pointsOperation === 'add' ? inputNum : -inputNum,
        note: 'Ajuste desde historial',
        notifyWhatsApp,
      });
      const balance = typeof out.balance === 'number' ? out.balance : newTotalPoints;
      setClient(prev => prev ? { ...prev, points: balance } : prev);
      setPointsInputValue('');
      setShowPointsAdjust(false);
      setShowCustomPointsInput(false);
      setNotifyWhatsApp(true);
    })();

    toast.promise(adjustPromise, {
      loading: pointsOperation === 'add' ? 'Sumando...' : 'Restando...',
      success: `${pointsOperation === 'add' ? 'Sumados' : 'Restados'} ${isCashback ? '$' : ''}${inputNum}${isCashback ? '' : ' pts'} correctamente`,
      error: 'Error al ajustar puntos'
    });

    try {
      await adjustPromise;
      fetchPointsMovements();
    } catch (error) {
      console.error('Error adjusting points:', error);
    } finally {
      setPointsAdjustLoading(false);
    }
  };

  // ============================================================================
  // v1.7.0: ETIQUETAS
  // ============================================================================

  const handleAddTag = async () => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag || !clientId || !currentUser?.businessId || tagSaving) return;

    if (clientTags.includes(tag)) {
      toast.error('Esta etiqueta ya existe');
      setTagInput('');
      return;
    }

    setTagSaving(true);
    try {
      await clientsApi.addTag(clientId, tag);
      setClientTags(prev => [...prev, tag]);
      setTagInput('');
      toast.success(`Etiqueta "${tag}" agregada`);
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Error al agregar etiqueta');
    } finally {
      setTagSaving(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!clientId || !currentUser?.businessId || tagSaving) return;

    setTagSaving(true);
    try {
      await clientsApi.removeTag(clientId, tag);
      setClientTags(prev => prev.filter(t => t !== tag));
      toast.success(`Etiqueta "${tag}" eliminada`);
    } catch (error) {
      console.error('Error removing tag:', error);
      toast.error('Error al eliminar etiqueta');
    } finally {
      setTagSaving(false);
    }
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // ============================================================================
  // TOGGLE MARKETING OPT-OUT
  // ============================================================================
  const handleConfirmOptOut = async () => {
    if (!client || !clientId || !currentUser?.uid || !currentUser?.businessId || togglingOptOut) return;

    setShowOptOutModal(false);
    setTogglingOptOut(true);

    const togglePromise = (async () => {
      await clientsApi.setMarketingOptOut(clientId, true);
      setClient(prev => prev ? { ...prev, marketingOptOut: true } : prev);
    })();

    toast.promise(togglePromise, {
      loading: 'Dando de baja...',
      success: `${client.visitorName || 'Cliente'} dado de baja de marketing`,
      error: 'Error al dar de baja'
    });

    try { await togglePromise; } catch (error) { console.error("Error opt-out:", error); } finally { setTogglingOptOut(false); }
  };

  const handleRestoreMarketing = async () => {
    if (!client || !clientId || !currentUser?.uid || !currentUser?.businessId || togglingOptOut) return;

    setTogglingOptOut(true);

    const restorePromise = (async () => {
      await clientsApi.setMarketingOptOut(clientId, false);
      setClient(prev => prev ? { ...prev, marketingOptOut: false } : prev);
    })();

    toast.promise(restorePromise, {
      loading: 'Restaurando...',
      success: `${client.visitorName || 'Cliente'} restaurado a marketing`,
      error: 'Error al restaurar'
    });

    try { await restorePromise; } catch (error) { console.error("Error restore:", error); } finally { setTogglingOptOut(false); }
  };

  // ============================================================================
  // v1.8.0: ARCHIVAR / DESARCHIVAR (soft-delete reversible)
  // ============================================================================
  const handleConfirmArchive = async () => {
    if (!client || !clientId || !currentUser?.uid || !currentUser?.businessId || archiveLoading) return;
    setShowArchiveModal(false);
    setArchiveLoading(true);

    const archivePromise = (async () => {
      await clientsApi.setArchived(clientId, true);
      setClient(prev => prev ? { ...prev, archived: true } : prev);
    })();

    toast.promise(archivePromise, {
      loading: 'Archivando...',
      success: `${client.visitorName || 'Cliente'} archivado`,
      error: 'Error al archivar',
    });
    try { await archivePromise; } catch (e) { console.error('Error archive:', e); } finally { setArchiveLoading(false); }
  };

  const handleUnarchive = async () => {
    if (!client || !clientId || !currentUser?.uid || !currentUser?.businessId || archiveLoading) return;
    setArchiveLoading(true);

    const unarchivePromise = (async () => {
      await clientsApi.setArchived(clientId, false);
      setClient(prev => prev ? { ...prev, archived: false } : prev);
    })();

    toast.promise(unarchivePromise, {
      loading: 'Desarchivando...',
      success: `${client.visitorName || 'Cliente'} desarchivado`,
      error: 'Error al desarchivar',
    });
    try { await unarchivePromise; } catch (e) { console.error('Error unarchive:', e); } finally { setArchiveLoading(false); }
  };

  // ============================================================================
  // v1.8.0: EDITAR DATOS DEL CLIENTE (nombre / teléfono / email / género / cumpleaños)
  // ============================================================================
  const openEditModal = () => {
    if (!client) return;
    setEditName(client.visitorName || '');
    // countryCode viene SIN '+' desde clientsApi (splitStoredPhone) → calzar con el select.
    setEditCountryCode(client.countryCode ? `+${client.countryCode}`.replace('+549', '+54 9') : '+52');
    setEditPhone(client.phoneNumber || '');
    setEditEmail(client.email || '');
    setEditGender(client.gender || '');
    setEditBirthDay(client.birthDay != null ? String(client.birthDay) : '');
    setEditBirthMonth(client.birthMonth != null ? String(client.birthMonth) : '');
    setShowEditModal(true);
  };

  const handleEditClient = async () => {
    if (!client || !clientId || !currentUser?.businessId || editLoading) return;

    const name = editName.trim();
    const phone = editPhone.replace(/\D/g, '');
    const cc = editCountryCode.trim();

    // Validaciones (mismas reglas que el registro)
    if (!name) { toast.error('El nombre es requerido'); return; }
    const editCountry = PHONE_COUNTRIES.find((c) => c.prefix === cc) || PHONE_COUNTRIES[0];
    if (phone.length !== editCountry.nationalDigits) {
      toast.error(`El número de ${editCountry.name} debe tener exactamente ${editCountry.nationalDigits} dígitos`); return;
    }
    // Forma canónica que guarda base: MX/US 10 locales; otro país lada+nacional.
    const storedPhone = storedPhoneForCountry(editCountry.code, phone);
    if (editEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editEmail.trim())) {
      toast.error('El correo electrónico no es válido'); return;
    }
    let bDay: number | null = null;
    let bMonth: number | null = null;   // 1-12
    if (editBirthDay !== '' || editBirthMonth !== '') {
      bDay = parseInt(editBirthDay);
      bMonth = parseInt(editBirthMonth);
      if (isNaN(bDay) || bDay < 1 || bDay > 31) { toast.error('El día de nacimiento no es válido'); return; }
      if (isNaN(bMonth) || bMonth < 1 || bMonth > 12) { toast.error('El mes de nacimiento no es válido'); return; }
    }

    setEditLoading(true);
    const phoneChanged = phone !== client.phoneNumber || editCountry.cc !== client.countryCode;

    const savePromise = (async () => {
      // Si cambió el teléfono, verificar que no choque con otro cliente del negocio.
      if (phoneChanged) {
        const existing = await clientsApi.lookup(storedPhone);
        if (existing && existing.id !== clientId) {
          throw new Error(`El número ${cc} ${phone} ya pertenece a ${existing.visitorName || 'otro cliente'}`);
        }
      }

      await clientsApi.update(clientId, {
        visitorName: name,
        phoneNumber: storedPhone,
        email: editEmail.trim(),
        ...(bDay != null && bMonth != null ? { birthDay: bDay, birthMonth: bMonth } : {}),
      });

      setClient(prev => prev ? {
        ...prev,
        visitorName: name,
        phoneNumber: phone,
        countryCode: editCountry.cc,
        email: editEmail.trim() || null,
        gender: editGender || '',
        birthDay: bDay,
        birthMonth: bMonth,
      } : prev);
      setShowEditModal(false);
    })();

    toast.promise(savePromise, {
      loading: 'Guardando cambios...',
      success: 'Datos del cliente actualizados',
      error: (e) => (e instanceof Error ? e.message : 'Error al actualizar el cliente'),
    });

    try { await savePromise; } catch (error) { console.error('Error editing client:', error); } finally { setEditLoading(false); }
  };

  // ============================================================================
  // MODAL (Portal)
  // ============================================================================
  const OptOutModal = () => {
    if (!showOptOutModal || !client) return null;

    return createPortal(
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        aria-labelledby="optout-modal-title" 
        role="dialog" 
        aria-modal="true"
      >
        <div 
          className="modal-overlay fixed inset-0 bg-slate-900/20 backdrop-blur-sm" 
          onClick={() => setShowOptOutModal(false)}
        ></div>

        <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-sm modal-panel border border-slate-100 overflow-hidden">
          <div className="px-6 pt-6 flex justify-between items-start">
            <h3 className="text-lg font-bold text-content">Dar de baja</h3>
            <button 
              onClick={() => setShowOptOutModal(false)} 
              className="text-content-3 hover:text-content-2 p-1 hover:bg-canvas rounded-full transition-colors -mr-2 -mt-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="px-6 pb-6 pt-2">
            <p className="text-sm text-content-3 mb-6">
              ¿Dar de baja permanentemente a <strong className="text-content">{client.visitorName || client.phoneNumber}</strong> de todos los mensajes de marketing?
            </p>

            <div className="space-y-3">
              <button
                onClick={handleConfirmOptOut}
                className="w-full p-4 border border-red-100 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-200 text-left flex items-center transition-all group shadow-sm hover:shadow-md active:scale-[0.98]"
              >
                <div className="bg-surface border border-red-50 p-2.5 rounded-full mr-4 text-red-400 group-hover:text-red-600 dark:text-red-400 group-hover:border-red-100 transition-colors shadow-sm">
                  <UserMinus className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-content group-hover:text-red-700 dark:text-red-300">Confirmar baja</p>
                  <p className="text-xs text-content-3 mt-0.5">No recibirá felicitaciones ni campañas.</p>
                </div>
              </button>
            </div>
          </div>
          
          <div className="px-6 py-3 bg-surface-2/50 border-t border-line flex justify-center">
            <button
              onClick={() => setShowOptOutModal(false)}
              className="text-sm text-content-3 hover:text-content-2 font-medium transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Cabecera */}
        <div className="flex items-center justify-between animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-content-3 hover:text-content-2 mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" />Volver
            </button>
            <h1 className="text-2xl font-bold text-content flex items-center mt-1">
              <History className="h-6 w-6 mr-2 text-accent" />Historial de Visitas
            </h1>
          </div>
          <button 
            onClick={reloadVisits} 
            disabled={loading || visitLoading} 
            className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm leading-4 font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${visitLoading ? 'animate-spin' : ''}`} />
            {visitLoading ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>

        {/* Loading */}
        {loading && !client && (
          <div className="bg-surface shadow rounded-lg p-6 text-center">
            <BrandLoader size={36} />
            <p className="text-content-3 text-sm">Cargando perfil del cliente...</p>
          </div>
        )}

        {/* Perfil del cliente */}
        {client && (
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  {clientId && (
                    <ClientAvatar
                      clientId={clientId}
                      name={client.visitorName}
                      hasPhoto={!!client.avatarUrl}
                    />
                  )}
                  <ClientContactInfo
                    clientId={clientId}
                    clientName={client.visitorName}
                    phoneNumber={client.phoneNumber}
                    countryCode={client.countryCode}
                    email={client.email}
                    showName={true}
                    nameAsLink={false}
                    showAvatar={false}
                  />
                  {clientId && <GoogleReviewChip clientId={clientId} />}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* «No acumula» (19-oct-2026): chip ámbar si está marcado (clic = editar, ✕ = quitar); si no, botón discreto. */}
                  {accrualBlock ? (
                    <span data-testid="ficha-chip-no-acumula" title={accrualBlock.note || undefined}
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                      <button type="button" disabled={!canBlock} onClick={() => setShowBlockModal(true)} className="inline-flex items-center disabled:cursor-default">
                        <Ban className="h-3 w-3 mr-1" /> No acumula · {REASON_LABEL[accrualBlock.reason]}{accrualBlock.ends_on ? ` (${fmtHasta(accrualBlock.ends_on)})` : ''}
                      </button>
                      {canBlock && (
                        <button type="button" onClick={quitarBloqueo} disabled={unblocking} title="Quitar: vuelve a acumular" aria-label="Quitar «no acumula»"
                          className="ml-2 text-amber-500 hover:text-green-600 dark:hover:text-green-400 transition-colors disabled:opacity-50">
                          {unblocking ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </button>
                      )}
                    </span>
                  ) : canBlock && (
                    <button type="button" onClick={() => setShowBlockModal(true)} data-testid="btn-no-acumula"
                      title="Empleado, influencer o cuenta de prueba: no suma visitas"
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-content-2 bg-canvas hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-300 border border-line transition-colors">
                      <Ban className="h-3.5 w-3.5 mr-1.5" /> No acumula
                    </button>
                  )}
                  {/* Membresía (F1, 20-oct-2026): chip verde si tiene una viva (clic = renovar/cambiar, ✕ = cancelar); si no, botón discreto. Solo con el flag del negocio. */}
                  {membershipsOn && (membership ? (
                    <span data-testid="ficha-chip-membresia" title={membership.payment_note || undefined}
                      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${membership.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30' : 'bg-surface-2 text-content-3 border-line'}`}>
                      <button type="button" disabled={!canBlock} onClick={() => setShowMembershipModal(true)} className="inline-flex items-center disabled:cursor-default">
                        <BadgeCheck className="h-3 w-3 mr-1" /> {membership.plan_name} · {fmtVence(membership)}{membership.quota_total != null ? ` · ${membership.quota_left} de ${membership.quota_total}` : ''}
                      </button>
                      {canBlock && membership.status === 'active' && (
                        <button type="button" onClick={cancelarMembresia} disabled={cancellingMemb} title="Cancelar membresía" aria-label="Cancelar membresía"
                          className="ml-2 text-emerald-500 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50">
                          {cancellingMemb ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </button>
                      )}
                    </span>
                  ) : canBlock && (
                    <button type="button" onClick={() => setShowMembershipModal(true)} data-testid="btn-membresia"
                      title="Vender una membresía a este cliente"
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-content-2 bg-canvas hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-300 border border-line transition-colors">
                      <BadgeCheck className="h-3.5 w-3.5 mr-1.5" /> Membresía
                    </button>
                  ))}
                  {/* v1.8.0: Editar datos del cliente */}
                  <button
                    onClick={openEditModal}
                    title="Editar datos del cliente"
                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 border border-blue-200 dark:border-blue-500/30 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
                  </button>
                  {/* v1.8.0: Archivar / Desarchivar */}
                  {client.archived ? (
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200">
                      <Archive className="h-3 w-3 mr-1" /> Archivado
                      <button
                        onClick={handleUnarchive}
                        disabled={archiveLoading}
                        className="ml-2 text-amber-500 hover:text-green-600 dark:text-green-400 transition-colors disabled:opacity-50"
                        title="Desarchivar"
                      >
                        {archiveLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArchiveRestore className="h-3 w-3" />}
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setShowArchiveModal(true)}
                      disabled={archiveLoading}
                      title="Archivar cliente (ocultar de listas y mensajes)"
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-content-2 bg-canvas hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-300 border border-line transition-colors disabled:opacity-50"
                    >
                      {archiveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Archive className="h-3.5 w-3.5 mr-1.5" />}
                      Archivar
                    </button>
                  )}
                  {/* Badge de marketing opt-out */}
                  {client.marketingOptOut && (
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200">
                      <BellOff className="h-3 w-3 mr-1" /> Dado de baja
                      <button
                        onClick={handleRestoreMarketing}
                        disabled={togglingOptOut}
                        className="ml-2 text-red-500 hover:text-green-600 dark:text-green-400 transition-colors disabled:opacity-50"
                        title="Restaurar a marketing"
                      >
                        {togglingOptOut ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bell className="h-3 w-3" />}
                      </button>
                    </span>
                  )}
                  {/* Saldo/Puntos */}
                  <div className="text-right">
                    <p className="text-xs text-content-3">{isCashback ? 'Saldo' : 'Puntos'}</p>
                    <p className="text-xl font-bold flex items-center justify-end text-green-600 dark:text-green-400">
                      {isCashback ? (
                        <><DollarSign className="h-5 w-5 mr-1" />{client.points.toFixed(2)}</>
                      ) : (
                        <><Star className="h-5 w-5 mr-1" />{Math.floor(client.points)}</>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Botón / Panel de ajuste de puntos */}
              <div className="mt-4 pt-3 border-t border-line">
                {hasProAccess ? (
                  !showPointsAdjust ? (
                    <div className="flex flex-wrap gap-2">
                      {/* Registrar VISITA real (venta): check-in con % automático, cuenta
                          para Últimas Visitas/Inactivos y dispara el mensaje de visita. */}
                      <button
                        onClick={() => setShowRegisterVisit(true)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-accent-solid hover:bg-accent-solid/90 transition-colors"
                      >
                        <Coins className="h-3.5 w-3.5 mr-1.5" />
                        Registrar visita{isCashback ? ' (venta)' : ''}
                      </button>
                      {/* Tarjeta QR del cliente (23-oct-2026): solo si el negocio prendió el escáner. */}
                      {currentUser?.cardScanEnabled && (
                        <button
                          onClick={() => setShowCard(true)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-content-2 bg-surface-2 hover:bg-surface-3 border border-line transition-colors"
                          title="Ver, imprimir o mandar por WhatsApp la tarjeta QR de este cliente"
                        >
                          <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                          Tarjeta
                        </button>
                      )}
                      <button
                        onClick={() => { setShowPointsAdjust(true); setNotifyWhatsApp(true); }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 border border-blue-200 dark:border-blue-500/30 transition-colors"
                      >
                        <Coins className="h-3.5 w-3.5 mr-1.5" />
                        Ajustar {isCashback ? 'saldo' : 'puntos'}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-blue-50 dark:bg-blue-500/10 rounded-lg p-4 border border-blue-200 animate-fade-in-up">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 flex items-center">
                          <Coins className="h-4 w-4 mr-1.5" />
                          Ajustar {isCashback ? 'saldo' : 'puntos'}
                        </h4>
                        <button
                          onClick={() => { setShowPointsAdjust(false); setPointsInputValue(''); setShowCustomPointsInput(false); }}
                          className="text-content-3 hover:text-content-2 p-1 rounded-full hover:bg-surface transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Toggle sumar/restar */}
                      <div className="flex rounded-md overflow-hidden border border-blue-200 w-fit mb-3">
                        <button
                          onClick={() => setPointsOperation('add')}
                          className={`px-3 py-2 text-xs font-medium flex items-center transition-colors ${
                            pointsOperation === 'add'
                              ? 'bg-green-500 text-white'
                              : 'bg-surface text-content-2 hover:bg-surface-2'
                          }`}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Sumar
                        </button>
                        <button
                          onClick={() => setPointsOperation('subtract')}
                          className={`px-3 py-2 text-xs font-medium flex items-center transition-colors ${
                            pointsOperation === 'subtract'
                              ? 'bg-red-500 text-white'
                              : 'bg-surface text-content-2 hover:bg-surface-2'
                          }`}
                        >
                          <Minus className="h-3 w-3 mr-1" /> Restar
                        </button>
                      </div>

                      {/* Preset pills + Personalizado */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {POINTS_ADJUST_PRESETS.map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => {
                              setPointsInputValue(String(preset));
                              setShowCustomPointsInput(false);
                            }}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                              !showCustomPointsInput && pointsInputValue === String(preset)
                                ? 'border-accent bg-accent/10 text-accent ring-1 ring-accent'
                                : 'border-line bg-surface text-content-2 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/10'
                            }`}
                          >
                            {isCashback ? `$${preset}` : `${preset} pts`}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomPointsInput(true);
                            setPointsInputValue('');
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                            showCustomPointsInput
                              ? 'border-accent bg-accent/10 text-accent ring-1 ring-accent'
                              : 'border-line bg-surface text-content-2 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/10'
                          }`}
                        >
                          Personalizado
                        </button>
                      </div>

                      {/* Input personalizado */}
                      {showCustomPointsInput && (
                        <div className="mb-3">
                          <div className="relative max-w-[200px]">
                            {isCashback && (
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-content-3 text-sm font-medium">$</span>
                            )}
                            <input
                              type="number"
                              value={pointsInputValue}
                              onChange={(e) => setPointsInputValue(e.target.value)}
                              placeholder={isCashback ? '0.00' : '0'}
                              min="0"
                              step={isCashback ? '0.01' : '1'}
                              autoFocus
                              className={`w-full border border-line rounded-md py-2 text-sm focus:ring-accent focus:border-blue-500 ${
                                isCashback ? 'pl-7 pr-3' : 'px-3'
                              }`}
                            />
                            {!isCashback && (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-content-3 text-xs">pts</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Aviso WhatsApp — solo sumas (las restas nunca notifican en backend) */}
                      {pointsOperation === 'add' && (
                        <div className="flex items-center mb-3">
                          <input
                            id="history-notify-whatsapp"
                            type="checkbox"
                            checked={notifyWhatsApp}
                            onChange={(e) => setNotifyWhatsApp(e.target.checked)}
                            disabled={pointsAdjustLoading}
                            className="h-4 w-4 rounded text-accent focus:ring-accent border-line"
                          />
                          <label htmlFor="history-notify-whatsapp" className="ml-2 text-xs text-content-2 cursor-pointer">
                            Avisar al cliente por WhatsApp
                            <span className="block text-[11px] text-content-3">Desmárcalo si es una corrección interna de saldo.</span>
                          </label>
                        </div>
                      )}

                      {/* Info del saldo actual + botón confirmar */}
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-accent">
                          Saldo actual: <span className="font-semibold">{formatPoints(client.points ?? 0)}</span>
                          {pointsInputValue && !isNaN(parseFloat(pointsInputValue)) && parseFloat(pointsInputValue) > 0 && (
                            <span className="ml-2">
                              → Nuevo: <span className="font-semibold">
                                {formatPoints(
                                  pointsOperation === 'add'
                                    ? (client.points ?? 0) + parseFloat(pointsInputValue)
                                    : Math.max(0, (client.points ?? 0) - parseFloat(pointsInputValue))
                                )}
                              </span>
                            </span>
                          )}
                        </p>

                        <button
                          onClick={handlePointsAdjust}
                          disabled={pointsAdjustLoading || !pointsInputValue || parseFloat(pointsInputValue) <= 0}
                          className="inline-flex items-center px-4 py-2 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {pointsAdjustLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          ) : (
                            <Check className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Confirmar
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex flex-wrap gap-2">
                  {/* La tarjeta QR es GRATIS (Mi Negocio la marca así): también en el plan gratis (Fabulositos, 27-oct-2026). */}
                  {currentUser?.cardScanEnabled && (
                    <button
                      onClick={() => setShowCard(true)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-content-2 bg-surface-2 hover:bg-surface-3 border border-line transition-colors"
                      title="Ver, imprimir o mandar por WhatsApp la tarjeta QR de este cliente"
                    >
                      <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                      Tarjeta
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/dashboard/subscription')}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-content-3 bg-surface-2 border border-line hover:bg-yellow-50 dark:hover:bg-yellow-500/10 hover:text-yellow-700 dark:hover:text-yellow-300 hover:border-yellow-200 dark:hover:border-yellow-500/30 transition-colors group"
                  >
                    <Lock className="h-3.5 w-3.5 mr-1.5 text-content-3 group-hover:text-yellow-500" />
                    Ajustar {isCashback ? 'saldo' : 'puntos'}
                    <Crown className="h-3 w-3 ml-1.5 text-content-3 group-hover:text-yellow-500" />
                  </button>
                  </div>
                )}
              </div>

              {/* Saldo de regalo en pesos (gift cards en modo dinero, 14-oct-2026). Solo si aplica. */}
              {clientId && <GiftBalanceCard clientId={clientId} />}

              {/* ✅ CÓDIGO DE INFLUENCER (Componente Extraído) */}
              {clientId && <InfluencerCodeManager clientId={clientId} clientPhone={client.phoneNumber} />}

              {/* ══════════════════════════════════════════════════════════════
                  v1.7.0: SECCIÓN DE ETIQUETAS
                  ══════════════════════════════════════════════════════════════ */}
              <div className="mt-4 pt-3 border-t border-line">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium text-content-2">Etiquetas</span>
                </div>

                {/* Chips de etiquetas existentes */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {clientTags.length > 0 ? (
                    clientTags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          disabled={tagSaving}
                          className="ml-1.5 text-purple-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-content-3 italic">Sin etiquetas</span>
                  )}
                </div>

                {/* Cashback especial por etiqueta (17-oct-2026): avisa a la caja que este cliente gana otro %. */}
                {(() => {
                  if (currentUser?.loyaltyMode !== 'cashback') return null;
                  const r = pctCashbackCliente(currentUser, clientTags, Number(currentUser?.cashbackPercentage ?? 10));
                  return r.especial ? (
                    <p className="mb-2 text-xs font-medium text-green-700 dark:text-green-400" data-testid="cashback-especial-ficha">
                      Cashback: {r.pct} % (especial)
                    </p>
                  ) : null;
                })()}

                {/* Input para agregar etiqueta */}
                <div className="flex gap-2 max-w-sm">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Agregar etiqueta..."
                    maxLength={30}
                    className="flex-1 border border-line rounded-md px-3 py-1.5 text-sm focus:ring-purple-500 focus:border-purple-500"
                  />
                  <button
                    onClick={handleAddTag}
                    disabled={!tagInput.trim() || tagSaving}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/10 hover:bg-purple-100 dark:hover:bg-purple-500/20 border border-purple-200 dark:border-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {tagSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Info adicional del cliente */}
              <div className="mt-4 border-t border-line pt-4">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
                  {client.email && (
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-content-3 flex items-center">
                        <Mail className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /> Email
                      </dt>
                      <dd className="mt-1 text-sm text-content pl-6">
                        <a href={getEmailLink(client.email)} className="text-content-2 hover:text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded" title={`Enviar email a ${client.email}`}>
                          {client.email}
                        </a>
                      </dd>
                    </div>
                  )}
                  
                  {client.gender && (
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-content-3 flex items-center">
                        <Users className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /> Género
                      </dt>
                      <dd className="mt-1 text-sm text-content pl-6">
                        {client.gender === 'male' ? 'Masculino' : client.gender === 'female' ? 'Femenino' : 'No especificado'}
                      </dd>
                    </div>
                  )}
                  
                  {client.birthDay !== null && client.birthMonth !== null && (
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-content-3 flex items-center">
                        <Cake className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /> Cumpleaños
                      </dt>
                      <dd className="mt-1 text-sm text-content pl-6">
                        {formatBirthday(client.birthDay, client.birthMonth - 1)}
                      </dd>
                    </div>
                  )}
                  
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-content-3 flex items-center">
                      <CalendarClock className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /> Última visita
                    </dt>
                    <dd className="mt-1 text-sm text-content pl-6">
                      {client.lastVisit ? <DateDisplay date={client.lastVisit} /> : 'Nunca'}
                    </dd>
                  </div>
                  
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-content-3 flex items-center">
                      <CalendarPlus className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /> Registro
                    </dt>
                    <dd className="mt-1 text-sm text-content pl-6">
                      <DateDisplay date={client.createdAt} />
                    </dd>
                  </div>

                  {/* Botón dar de baja */}
                  {!client.marketingOptOut && (
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-content-3 flex items-center">
                        <BellOff className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /> Marketing
                      </dt>
                      <dd className="mt-1 pl-6">
                        <button
                          onClick={() => setShowOptOutModal(true)}
                          disabled={togglingOptOut}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-content-2 bg-canvas hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
                        >
                          {togglingOptOut ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <BellOff className="h-3 w-3 mr-1" />
                          )}
                          Dar de baja
                        </button>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        )}

        {/* Historial de Visitas */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-line">
            <h3 className="text-lg leading-6 font-medium text-content flex items-center">
              <CalendarClock className="h-5 w-5 mr-2 text-content-3" /> Registro de Visitas
            </h3>
            {!loading && !visitLoading && (
              <span className="text-sm text-content-3">
                Total: {totalVisits} {totalVisits === 1 ? 'visita' : 'visitas'}
              </span>
            )}
          </div>
          <div className="border-t border-line">
            {visitLoading && checkIns.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-content-3">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                Cargando historial...
              </div>
            ) : !visitLoading && checkIns.length === 0 && !loading ? (
              <div className="px-4 py-6 text-center text-sm text-content-3">
                <Clock className="h-8 w-8 mx-auto mb-2 text-content-3" />
                No hay visitas registradas para este cliente.
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {checkIns.map((checkIn) => {
                  const checkInDate = checkIn.checkInDate instanceof Date ? checkIn.checkInDate : new Date(checkIn.checkInDate);
                  const isValid = !isNaN(checkInDate.getTime());
                  
                  return (
                    <li key={checkIn.id || `${checkIn.checkInDate}`} className="px-4 py-3 hover:bg-surface-2 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          <div className="flex-shrink-0 mr-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                              checkIn.feedback === 'malo' ? 'bg-red-100 dark:bg-red-500/20' :
                              checkIn.feedback === 'excelente' ? 'bg-yellow-100 dark:bg-yellow-500/20' :
                              'bg-blue-100 dark:bg-blue-500/20'
                            }`}>
                              <MessageCircle className={`h-4 w-4 ${getFeedbackColor(checkIn.feedback)}`} />
                            </div>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-content">
                              {isValid ? (
                                <DateDisplay date={checkInDate} />
                              ) : (
                                <span className="text-content-3 italic">Fecha inválida</span>
                              )}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-xs ${getFeedbackColor(checkIn.feedback)}`}>
                                {getFeedbackText(checkIn.feedback)}
                              </span>
                              {checkIn.branchName && (
                                <span className="text-xs text-content-3">
                                  · {checkIn.branchName}
                                </span>
                              )}
                            </div>
                            {checkIn.cancelledAt && (
                              <p className="mt-0.5 text-xs text-red-600 dark:text-red-400" data-testid="historial-cancelada">
                                Cancelada{checkIn.cancelledByName ? ` por ${checkIn.cancelledByName}` : ''}{checkIn.cancelReason ? `: ${checkIn.cancelReason}` : ''}
                              </p>
                            )}
                            {checkIn.accrualBlockReason && (
                              <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300" data-testid="historial-no-acumula">
                                Sin puntos: no acumula ({REASON_LABEL[checkIn.accrualBlockReason]})
                              </p>
                            )}
                            {/* Venta · folio · quién atendió (Bramarket 10-oct-2026) */}
                            {(checkIn.ticketRef || checkIn.userName || (checkIn.amount ?? 0) > 0) && (
                              <p className="text-xs text-content-3 mt-0.5" data-testid="historial-detalle">
                                {[
                                  (checkIn.amount ?? 0) > 0 ? `Venta $${(checkIn.amount as number).toFixed(2)}` : '',
                                  checkIn.ticketRef ? `Folio ${checkIn.ticketRef}` : '',
                                  checkIn.userName ? `Atendió ${checkIn.userName}` : '',
                                ].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className={`text-sm font-semibold ${checkIn.cancelledAt ? 'line-through text-content-3' : checkIn.earnedPoints > 0 ? 'text-green-600 dark:text-green-400' : 'text-content-3'}`}>
                            {checkIn.earnedPoints > 0 ? (
                              isCashback ? `+$${checkIn.earnedPoints.toFixed(2)}` : `+${Math.floor(checkIn.earnedPoints)} pts`
                            ) : (
                              isCashback ? '$0.00' : '0 pts'
                            )}
                          </p>
                          {checkIn.totalPointsAfterCheckIn !== undefined && checkIn.totalPointsAfterCheckIn !== null && (
                            <p className="text-xs text-content-3">
                              Saldo: {isCashback ? `$${checkIn.totalPointsAfterCheckIn.toFixed(2)}` : `${Math.floor(checkIn.totalPointsAfterCheckIn)}`}
                            </p>
                          )}
                          {canCancelVisit && !checkIn.cancelledAt && (
                            <button type="button" onClick={() => setCancelCheckIn(checkIn)} data-testid="btn-cancelar-compra"
                              className="mt-1 text-[11px] text-content-3 hover:text-red-600 hover:underline">
                              Cancelar compra
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            
            {hasMoreVisits && !visitLoading && checkIns.length > 0 && (
              <div className="px-4 py-3 bg-surface-2 text-center border-t border-line">
                <button onClick={loadMoreVisits} className="text-sm font-medium text-accent hover:opacity-80">
                  Cargar más visitas...
                </button>
              </div>
            )}
            
            {visitLoading && checkIns.length > 0 && (
              <div className="px-4 py-3 bg-surface-2 text-center border-t border-line">
                <RefreshCw className="h-4 w-4 animate-spin inline mr-2 text-accent" />
                <span className="text-sm text-content-3">Cargando...</span>
              </div>
            )}
          </div>
        </div>

        {/* Historial de Movimientos de Puntos */}
        {pointsMovements.length > 0 && (
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-line">
              <h3 className="text-lg leading-6 font-medium text-content flex items-center">
                <ArrowUpDown className="h-5 w-5 mr-2 text-content-3" /> Ajustes y Bonos
              </h3>
              <span className="text-sm text-content-3">
                {pointsMovements.length} {pointsMovements.length === 1 ? 'movimiento' : 'movimientos'}
              </span>
            </div>
            <div className="border-t border-line">
              {movementsLoading ? (
                <div className="px-4 py-6 text-center text-sm text-content-3">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Cargando movimientos...
                </div>
              ) : (
                <ul className="divide-y divide-line">
                  {pointsMovements.map((movement) => {
                    const isAdd = movement.operation === 'add';
                    const isReferral = movement.source?.startsWith('referral_');
                    const isGiftCard = movement.source === 'gift_card';
                    return (
                      <li key={movement.id} className="px-4 py-3 hover:bg-surface-2 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-w-0">
                            <div className="flex-shrink-0 mr-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                isGiftCard ? 'bg-teal-100 dark:bg-teal-500/20' :
                                isReferral ? 'bg-purple-100 dark:bg-purple-500/20' :
                                isAdd ? 'bg-green-100 dark:bg-green-500/20' : 'bg-red-100 dark:bg-red-500/20'
                              }`}>
                                {isGiftCard ? (
                                  <CreditCard className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                                ) : isReferral ? (
                                  <Gift className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                ) : isAdd ? (
                                  <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
                                )}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-content">
                                <DateDisplay date={movement.createdAt} />
                              </p>
                              <p className="text-xs text-content-3 mt-0.5">
                                {getSourceLabel(movement.source, movement.description)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <p className={`text-sm font-semibold ${
                              isGiftCard ? 'text-teal-600 dark:text-teal-400' :
                              isReferral ? 'text-purple-600 dark:text-purple-400' :
                              isAdd ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {isAdd ? '+' : '-'}
                              {isCashback
                                ? `$${Math.abs(movement.points).toFixed(2)}`
                                : `${Math.abs(Math.floor(movement.points))} pts`
                              }
                            </p>
                            {movement.previousPoints > 0 || movement.newPoints > 0 ? (
                              <p className="text-xs text-content-3">
                                {formatPoints(movement.previousPoints)} → {formatPoints(movement.newPoints)}
                              </p>
                            ) : isReferral ? (
                              <p className="text-xs text-purple-400">Referido</p>
                            ) : isGiftCard ? (
                              <p className="text-xs text-teal-400">Gift Card</p>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Loading de movimientos (cuando aún no hay visitas cargadas) */}
        {movementsLoading && pointsMovements.length === 0 && !loading && (
          <div className="bg-surface shadow rounded-lg p-6 text-center animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-content-3" />
            <p className="text-content-3 text-sm">Cargando movimientos de puntos...</p>
          </div>
        )}
      </div>

      <OptOutModal />

      {/* v1.8.0: MODAL CONFIRMAR ARCHIVAR */}
      {showArchiveModal && client && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="modal-overlay fixed inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setShowArchiveModal(false)}></div>
          <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-sm border border-slate-100 overflow-hidden modal-panel">
            <div className="px-6 pt-6 flex justify-between items-start">
              <h3 className="text-lg font-bold text-content flex items-center"><Archive className="h-5 w-5 mr-2 text-amber-500" /> Archivar cliente</h3>
              <button onClick={() => setShowArchiveModal(false)} className="text-content-3 hover:text-content-2 p-1 hover:bg-canvas rounded-full transition-colors -mr-2 -mt-2"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 pb-6 pt-2">
              <p className="text-sm text-content-3 mb-6">
                Se ocultará a <strong className="text-content">{client.visitorName || client.phoneNumber}</strong> de las listas, búsquedas y mensajes (campañas, cumpleaños). <strong className="text-content">Conserva su saldo e historial</strong> y puedes desarchivarlo cuando quieras.
              </p>
              <button
                onClick={handleConfirmArchive}
                className="w-full p-4 border border-amber-100 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:border-amber-200 text-left flex items-center transition-all group shadow-sm hover:shadow-md active:scale-[0.98]"
              >
                <div className="bg-surface border border-amber-50 p-2.5 rounded-full mr-4 text-amber-400 group-hover:text-amber-600 group-hover:border-amber-100 transition-colors shadow-sm">
                  <Archive className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-content group-hover:text-amber-700 dark:text-amber-300">Confirmar archivar</p>
                  <p className="text-xs text-content-3 mt-0.5">Reversible. No recibirá mensajes mientras esté archivado.</p>
                </div>
              </button>
            </div>
            <div className="px-6 py-3 bg-surface-2/50 border-t border-line flex justify-center">
              <button onClick={() => setShowArchiveModal(false)} className="text-sm text-content-3 hover:text-content-2 font-medium transition-colors">Cancelar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* v1.8.0: MODAL EDITAR CLIENTE (inline para no remontar inputs) */}
      {showEditModal && client && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          aria-labelledby="edit-client-modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-overlay fixed inset-0 bg-slate-900/20 backdrop-blur-sm"
            onClick={() => !editLoading && setShowEditModal(false)}
          ></div>

          <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md modal-panel border border-slate-100 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 pt-6 flex justify-between items-start">
              <h3 id="edit-client-modal-title" className="text-lg font-bold text-content flex items-center">
                <Pencil className="h-5 w-5 mr-2 text-blue-500" /> Editar cliente
              </h3>
              <button
                onClick={() => !editLoading && setShowEditModal(false)}
                className="text-content-3 hover:text-content-2 p-1 hover:bg-canvas rounded-full transition-colors -mr-2 -mt-2"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 pb-6 pt-4 space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full border border-line rounded-md px-3 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre del cliente"
                />
              </div>

              {/* Teléfono (país + número) */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1 flex items-center">
                  <Phone className="h-3.5 w-3.5 mr-1.5 text-content-3" /> Teléfono
                </label>
                <div className="flex gap-2">
                  <select
                    value={editCountryCode}
                    onChange={(e) => setEditCountryCode(e.target.value)}
                    className="border border-line rounded-md px-2 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                  >
                    {countriesFor(currentUser?.intlPhones).map(c => (
                      <option key={c.code} value={c.prefix}>{c.flag} {c.prefix} ({c.code})</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={editPhone}
                    onChange={(e) => {
                      const max = (PHONE_COUNTRIES.find((c) => c.prefix === editCountryCode) || PHONE_COUNTRIES[0]).nationalDigits;
                      setEditPhone(e.target.value.replace(/\D/g, '').slice(0, max));
                    }}
                    className="flex-1 border border-line rounded-md px-3 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`${(PHONE_COUNTRIES.find((c) => c.prefix === editCountryCode) || PHONE_COUNTRIES[0]).nationalDigits} dígitos`}
                    maxLength={(PHONE_COUNTRIES.find((c) => c.prefix === editCountryCode) || PHONE_COUNTRIES[0]).nationalDigits}
                  />
                </div>
                <p className="text-xs text-content-3 mt-1">Cambiar el teléfono no afecta el saldo ni el historial.</p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1 flex items-center">
                  <Mail className="h-3.5 w-3.5 mr-1.5 text-content-3" /> Email <span className="text-content-3 ml-1 font-normal">(opcional)</span>
                </label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="w-full border border-line rounded-md px-3 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                  placeholder="correo@ejemplo.com"
                />
              </div>

              {/* Género */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1 flex items-center">
                  <Users className="h-3.5 w-3.5 mr-1.5 text-content-3" /> Género <span className="text-content-3 ml-1 font-normal">(opcional)</span>
                </label>
                <select
                  value={editGender}
                  onChange={(e) => setEditGender(e.target.value)}
                  className="w-full border border-line rounded-md px-3 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No especificado</option>
                  <option value="male">Masculino</option>
                  <option value="female">Femenino</option>
                </select>
              </div>

              {/* Cumpleaños */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1 flex items-center">
                  <Cake className="h-3.5 w-3.5 mr-1.5 text-content-3" /> Cumpleaños <span className="text-content-3 ml-1 font-normal">(opcional)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={editBirthDay}
                    onChange={(e) => setEditBirthDay(e.target.value)}
                    min={1}
                    max={31}
                    className="w-24 border border-line rounded-md px-3 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Día"
                  />
                  <select
                    value={editBirthMonth}
                    onChange={(e) => setEditBirthMonth(e.target.value)}
                    className="flex-1 border border-line rounded-md px-3 py-2 text-sm bg-surface focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Mes</option>
                    {MONTHS.map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 bg-surface-2/50 border-t border-line flex justify-end gap-2">
              <button
                onClick={() => !editLoading && setShowEditModal(false)}
                disabled={editLoading}
                className="px-4 py-2 text-sm text-content-3 hover:text-content-2 font-medium transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditClient}
                disabled={editLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
                Guardar cambios
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Registrar visita real (check-in) — refresca saldo y movimientos al terminar */}
      {showRegisterVisit && client && (
        <RegisterVisitModal
          client={{ id: String(client.id), name: client.visitorName, phone: client.phoneNumber, tags: clientTags }}
          onClose={() => setShowRegisterVisit(false)}
          onDone={({ balance }) => {
            setClient(prev => prev ? { ...prev, points: balance } : prev);
            fetchPointsMovements();
          }}
        />
      )}

      {showCard && client && (
        <ClientCardModal
          client={{ id: String(client.id), name: client.visitorName, phone: client.phoneNumber }}
          onClose={() => setShowCard(false)}
        />
      )}

      {showBlockModal && client && clientId && (
        <AccrualBlockModal
          contact={{ id: clientId, name: client.visitorName || 'Cliente' }}
          current={accrualBlock}
          onClose={() => setShowBlockModal(false)}
          onSaved={(b) => { setAccrualBlock(b); setShowBlockModal(false); toast.success(`${client.visitorName || 'Cliente'} ya no acumula visitas`); }}
        />
      )}
      {showMembershipModal && client && clientId && (
        <SellMembershipModal
          contact={{ id: clientId, name: client.visitorName || 'Cliente' }}
          current={membership}
          onClose={() => setShowMembershipModal(false)}
          onSaved={(f) => { setMembership(f.current); setShowMembershipModal(false); toast.success(f.action === 'renewal' ? 'Membresía renovada' : 'Membresía vendida'); }}
        />
      )}
      {cancelCheckIn && client && (
        <CancelVisitModal
          visit={{
            id: cancelCheckIn.id, clientName: client.visitorName, amount: cancelCheckIn.amount, ticketRef: cancelCheckIn.ticketRef,
            earnedPoints: cancelCheckIn.earnedPoints,
            dateLabel: new Date(cancelCheckIn.checkInDate).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
          }}
          isCashback={isCashback}
          onClose={() => setCancelCheckIn(null)}
          onCancelled={({ balance, negative }) => {
            setCancelCheckIn(null);
            setClient(prev => prev ? { ...prev, points: balance } : prev);
            toast.success(negative ? `Compra cancelada. Su saldo quedó en ${formatPoints(balance)} (negativo).` : 'Compra cancelada.');
            reloadVisits();
            fetchPointsMovements();
          }}
        />
      )}
    </Layout>
  );
};

export default ClientHistoryPage;